import type { TemplateItem } from "../types.ts";

export function childrenCount(items: TemplateItem[]): Map<string, number> {
	const counts = new Map<string, number>();
	for (const item of items) {
		if (!item.parentId) continue;
		counts.set(item.parentId, (counts.get(item.parentId) ?? 0) + 1);
	}
	return counts;
}

export function descendantIds(items: TemplateItem[], id: string): Set<string> {
	const ids = new Set<string>();
	const root = items.find((item) => item.id === id);
	if (!root) return ids;
	for (const item of items.slice(items.indexOf(root) + 1)) {
		if (item.depth <= root.depth) break;
		ids.add(item.id);
	}
	return ids;
}

function blockOf(items: TemplateItem[], id: string): TemplateItem[] {
	const start = items.findIndex((item) => item.id === id);
	const root = items[start];
	if (!root) return [];
	const block = [root];
	for (const item of items.slice(start + 1)) {
		if (item.depth <= root.depth) break;
		block.push(item);
	}
	return block;
}

function arrayMove<T>(items: T[], from: number, to: number): T[] {
	const next = [...items];
	const [moved] = next.splice(from, 1);
	if (moved !== undefined) next.splice(to, 0, moved);
	return next;
}

export interface SiblingMove {
	items: TemplateItem[];
	changes: { itemId: string; position: number }[];
}

export function moveSiblingBlock(
	items: TemplateItem[],
	activeId: string,
	overId: string,
): SiblingMove | null {
	const active = items.find((item) => item.id === activeId);
	const over = items.find((item) => item.id === overId);
	if (!active || !over || active.id === over.id) return null;
	if ((active.parentId ?? "") !== (over.parentId ?? "")) return null;

	const siblings = items.filter(
		(item) => (item.parentId ?? "") === (active.parentId ?? ""),
	);
	const reordered = arrayMove(
		siblings,
		siblings.indexOf(active),
		siblings.indexOf(over),
	);

	const blocks = reordered.flatMap((sibling) => blockOf(items, sibling.id));
	const first = siblings[0];
	if (!first) return null;
	const regionStart = items.indexOf(first);
	const next = [
		...items.slice(0, regionStart),
		...blocks,
		...items.slice(regionStart + blocks.length),
	];

	const changes = reordered
		.map((sibling, index) => ({ itemId: sibling.id, position: index }))
		.filter(({ itemId, position }) => {
			const sibling = siblings.find((item) => item.id === itemId);
			return sibling?.position !== position;
		});

	return { items: next, changes };
}

export function visibleItems(
	items: TemplateItem[],
	collapsed: Set<string>,
): TemplateItem[] {
	const visible: TemplateItem[] = [];
	let skipDeeperThan: number | null = null;
	for (const item of items) {
		if (skipDeeperThan !== null && item.depth > skipDeeperThan) continue;
		skipDeeperThan = collapsed.has(item.id) ? item.depth : null;
		visible.push(item);
	}
	return visible;
}
